// Modules
import { Store, select } from '@ngrx/store';
import { formatDate } from '../../../../../../shared/utils/date-utils';
import { Component, Input, OnInit, EventEmitter, Output } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

//Interfaces
import {
  IAccidentDetail,
  IAccidentEditableData,
} from '@accidents/shared/interfaces/accidents';
import { ICustomDropdown } from '@shared/interfaces';

//Constants
import {
  DATA_TABS,
  TAB_ACTION,
  TIPO_DOCUMENTO,
} from '@accidents/shared/constants';

// Store
import { ModuleState } from '@app/modules/accidents/store/reducers';
import * as DetailAccidentSelectors from '@accidents/store/selectors/detail-accident.selectors';

// Validators
import { ValidateCarRegistration } from '@app/modules/shared/validators/carRegistration.validator';
import { ValidateChasisNumber } from '@app/modules/shared/validators/carChasis';
import { ValidateTime } from '@app/modules/shared/validators/time.validator';

@Component({
  selector: 'sbi-cot-back-accident-data-tab',
  templateUrl: './accident-data-tab.component.html',
  styleUrls: ['./accident-data-tab.component.scss'],
  standalone: false,
})
export class AccidentDataTabComponent implements OnInit {
  //pubic variables
  public tabs = DATA_TABS;
  public activeTab = DATA_TABS.DATA_ACCIDENT;
  public tabActionList = TAB_ACTION;
  public loading: boolean;
  public accidentEditableData: IAccidentEditableData = {};
  public errorEstadoFallido: string = null;
  public documentTypes: ICustomDropdown[] = [];
  public accidentDataForm: FormGroup;
  public accidentDetail: IAccidentDetail;
  @Input() tabAction: string;
  @Input() puedeEditarMatriculaYChasis: boolean = false;
  @Input() puedeEditarFechaHoraOcurrencia: boolean = false;
  @Input() attemptedReprocess: boolean = false;
  public canEditFechaHoraOcurrencia: boolean = false;
  public maxFechaOcurrencia: Date = null;

  // Outputs
  @Output() editAccidentTabData = new EventEmitter();

  // Servicio detalle siniestros
  private _accidentDetail$ = this._store$.pipe(
    select(DetailAccidentSelectors.selectDetailAccident)
  );

  constructor(
    private _formBuilder: FormBuilder,
    private _store$: Store<ModuleState>
  ) {}

  ngOnInit() {
    this._parseDatosAccidente();

    this._setDatosSiniestrosForm();

    this._parseDatosSiniestro();

    this._updateEditAccidenteData();
    this._updateEditMatricula();
    this._updateEditFechaHoraOcurrencia();

    //Set data dropdown tipo documento
    this._setDocumentTypeDropdownData();
  }

  // Private method to set the form data
  private _parseDatosAccidente(): void {
    this._accidentDetail$.subscribe((accidentRequest: IAccidentDetail) => {
      if (accidentRequest) {
        this.accidentDetail = accidentRequest;
      }
    });
  }

  //Method to set the document type dropdown data
  private _setDocumentTypeDropdownData(): void {
    this.documentTypes = TIPO_DOCUMENTO.map(el => ({
      name: el,
      value: el,
    }));
  }

  // Private method for build the form
  private _setDatosSiniestrosForm(): void {
    this.canEditFechaHoraOcurrencia =
      this.tabAction === this.tabActionList.EDIT &&
      this.puedeEditarFechaHoraOcurrencia;

    this.accidentDataForm = this._formBuilder.group({
      serviceCallId: [{ value: '', disabled: true }],
      matricula: [
        {
          value: '',
          disabled: !(
            this.tabAction === this.tabActionList.EDIT &&
            this.puedeEditarMatriculaYChasis
          ),
        },
        ValidateCarRegistration,
      ],
      causaSiniestro: [
        {
          value: '',
          disabled: true,
        },
      ],
      numeroChasis: [
        {
          value: '',
          disabled: !(
            this.tabAction === this.tabActionList.EDIT &&
            this.puedeEditarMatriculaYChasis
          ),
        },
        ValidateChasisNumber,
      ],
      origenDenuncia: [{ value: '', disabled: true }],
      estado: [{ value: '', disabled: true }],
      subEstado: [{ value: '', disabled: true }],
      liquidadorInterno: [{ value: '', disabled: true }],
      fechaDeOcurrencia: [
        { value: '', disabled: !this.canEditFechaHoraOcurrencia },
        [Validators.required, this._validateMaxFechaOcurrencia.bind(this)],
      ],
      horaDeOcurrencia: [
        { value: '', disabled: !this.canEditFechaHoraOcurrencia },
        [Validators.required, ValidateTime],
      ],
      fechaDeDenuncia: [{ value: '', disabled: true }],
      serieServiceCall: [{ value: '', disabled: true }],
      subclaseNegocio: [{ value: '', disabled: true }],
      coberturaConfirmada: [{ value: '', disabled: true }],
      codigoCausa: [{ value: '', disabled: true }],
      descripcionCausa: [{ value: '', disabled: true }],
      fechaDeApertura: [{ value: '', disabled: true }],
      envioNotificacionWhatsapp: [{ value: '', disabled: true }],
      envioNotificacionEmail: [{ value: '', disabled: true }],
      comentariosAdicionales: [{ value: '', disabled: true }],
      descripcionDelHecho: [{ value: '', disabled: true }],

      //Datos primer testigo
      primerTestigoNombre: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      primerTestigoApellido: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      primerTestigoTipoDocumento: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      primerTestigoNroDocumento: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      primerTestigoEmail: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      primerTestigoTelefono: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      primerTestigoDireccion: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      //Datos segundo testigo
      segundoTestigoNombre: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      segundoTestigoApellido: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      segundoTestigoTipoDocumento: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      segundoTestigoNroDocumento: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      segundoTestigoEmail: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      segundoTestigoTelefono: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      segundoTestigoDireccion: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      segundoTestigoAmpliacionTestigos: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      //Datos cuestionario
      cuestionarioFechaLlegada: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioFechaHurto: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioLugar: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioNroJuegoLlaves: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioCantidadLlaves: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioIndiciosForcejeo: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioSistemasSeguridad: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioCantidadActivadores: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioExtravioJuegoLlaves: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioOtraPersona: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioOtraPersonaDuranteHurto: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioColorVehiculo: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioCirculacionVehiculo: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioFrenteRadio: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioMarcaRadio: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
      cuestionarioObservaciones: [
        { value: '', disabled: this.tabAction === this.tabActionList.VIEW },
      ],
    });
  }

  // Private method to set the form data
  private _parseDatosSiniestro(): void {
    // Get form data
    const causaSiniestro = this.accidentDataForm.get('causaSiniestro');
    const serviceCallId = this.accidentDataForm.get('serviceCallId');
    const matricula = this.accidentDataForm.get('matricula');
    const numeroChasis = this.accidentDataForm.get('numeroChasis');
    const origenDenuncia = this.accidentDataForm.get('origenDenuncia');
    const estado = this.accidentDataForm.get('estado');
    const subEstado = this.accidentDataForm.get('subEstado');
    const liquidadorInterno = this.accidentDataForm.get('liquidadorInterno');
    const fechaDeOcurrencia = this.accidentDataForm.get('fechaDeOcurrencia');
    const horaDeOcurrencia = this.accidentDataForm.get('horaDeOcurrencia');
    const fechaDeDenuncia = this.accidentDataForm.get('fechaDeDenuncia');
    const serieServiceCall = this.accidentDataForm.get('serieServiceCall');
    const subclaseNegocio = this.accidentDataForm.get('subclaseNegocio');
    const coberturaConfirmada = this.accidentDataForm.get(
      'coberturaConfirmada'
    );
    const codigoCausa = this.accidentDataForm.get('codigoCausa');
    const descripcionCausa = this.accidentDataForm.get('descripcionCausa');
    const fechaDeApertura = this.accidentDataForm.get('fechaDeApertura');
    const envioNotificacionWhatsapp = this.accidentDataForm.get(
      'envioNotificacionWhatsapp'
    );
    const envioNotificacionEmail = this.accidentDataForm.get(
      'envioNotificacionEmail'
    );
    const comentariosAdicionales = this.accidentDataForm.get(
      'comentariosAdicionales'
    );
    const descripcionDelHecho = this.accidentDataForm.get(
      'descripcionDelHecho'
    );

    // Rellenamos el Estado
    causaSiniestro.patchValue(this.accidentDetail.causaDelSiniestro);
    matricula.patchValue(
      this.accidentDetail.datosEdicion?.matricula
        ? this.accidentDetail.datosEdicion.matricula
        : this.accidentDetail.vehiculo.matricula
    );
    serviceCallId.patchValue(this.accidentDetail.serviceCallId);
    numeroChasis.patchValue(
      this.accidentDetail.datosEdicion
        ? this.accidentDetail.datosEdicion.nroChasis
        : this.accidentDetail.vehiculo.chasis
    );
    origenDenuncia.patchValue('3');
    estado.patchValue('Abierto');
    subEstado.patchValue('Pendiente');
    liquidadorInterno.patchValue(this.accidentDetail.assigneeCode);

    if (this.accidentDetail.fechaDeDenuncia) {
      this.maxFechaOcurrencia = new Date(this.accidentDetail.fechaDeDenuncia);
    }

    const fechaOcurrencia = this.accidentDetail.datosEdicion?.fechaOcurrencia
      ? this.accidentDetail.datosEdicion.fechaOcurrencia
      : this.accidentDetail.fechaDeOcurrencia;
    if (fechaOcurrencia) {
      if (this.canEditFechaHoraOcurrencia) {
        const fecha = new Date(fechaOcurrencia);
        const esFechaValida =
          !this.maxFechaOcurrencia || fecha <= this.maxFechaOcurrencia;
        // Si la fecha original es la causa de la inconsistencia (posterior a
        // la fecha de denuncia), dejamos el campo vacío para que el usuario
        // deba seleccionar una fecha válida, en vez de mostrar un valor que
        // el datepicker no puede representar por el maxDate configurado
        fechaDeOcurrencia.patchValue(esFechaValida ? fecha : null);
      } else {
        fechaDeOcurrencia.patchValue(formatDate(fechaOcurrencia, 'dd/MM/yyyy'));
      }
      horaDeOcurrencia.patchValue(formatDate(fechaOcurrencia, 'HH:mm:ss'));
    }

    fechaDeDenuncia.patchValue(
      formatDate(this.accidentDetail.fechaDeDenuncia, 'dd/MM/yyyy')
    );
    serieServiceCall.patchValue('238');
    subclaseNegocio.patchValue(
      this._nameSubClass(this.accidentDetail?.datosPoliza?.subClass)
    );
    coberturaConfirmada.patchValue(true);
    codigoCausa.patchValue('');
    descripcionCausa.patchValue('');
    fechaDeApertura.patchValue(
      formatDate(this.accidentDetail.fechaDeLlamada, 'dd/MM/yyyy')
    );
    envioNotificacionWhatsapp.patchValue(true);
    envioNotificacionEmail.patchValue(true);
    if (this.accidentDetail.datosSiniestro) {
      comentariosAdicionales.patchValue(
        this.accidentDetail.datosSiniestro.comentariosAdicionalesDelDenunciante
      );
      descripcionDelHecho.patchValue(
        this.accidentDetail.datosSiniestro.descripcionDelHecho
      );
    }

    this._parseDatosAccidente_PrimerTestigo();
    this._parseDatosAccidente_SegundoTestigo();
    this._parseDatosAccidente_Cuestionario();
  }

  private _nameSubClass(subClass: string): string {
    if (subClass === 'Automoviles') {
      return 'Automóviles';
    }
    return subClass || 'Automóviles';
  }

  private _parseDatosAccidente_PrimerTestigo(): void {
    // Get form data
    const primerTestigoNombre = this.accidentDataForm.get(
      'primerTestigoNombre'
    );
    const primerTestigoApellido = this.accidentDataForm.get(
      'primerTestigoApellido'
    );
    const primerTestigoTipoDocumento = this.accidentDataForm.get(
      'primerTestigoTipoDocumento'
    );
    const primerTestigoNroDocumento = this.accidentDataForm.get(
      'primerTestigoNroDocumento'
    );
    const primerTestigoEmail = this.accidentDataForm.get('primerTestigoEmail');
    const primerTestigoTelefono = this.accidentDataForm.get(
      'primerTestigoTelefono'
    );
    const primerTestigoDireccion = this.accidentDataForm.get(
      'primerTestigoDireccion'
    );

    //Mapeo de los campos
    if (this.accidentDetail.primerTestigo) {
      primerTestigoNombre.patchValue(this.accidentDetail.primerTestigo.nombre);
      primerTestigoApellido.patchValue(
        this.accidentDetail.primerTestigo.apellido
      );
      primerTestigoTipoDocumento.patchValue(
        this.accidentDetail.primerTestigo.tipoDocumento
      );
      primerTestigoNroDocumento.patchValue(
        this.accidentDetail.primerTestigo.nroDocumento
      );
      primerTestigoEmail.patchValue(this.accidentDetail.primerTestigo.email);
      primerTestigoTelefono.patchValue(
        this.accidentDetail.primerTestigo.telefono
      );
      primerTestigoDireccion.patchValue(
        this.accidentDetail.primerTestigo.direccion
      );
    }
  }

  private _parseDatosAccidente_SegundoTestigo(): void {
    // Get form data
    const segundoTestigoNombre = this.accidentDataForm.get(
      'segundoTestigoNombre'
    );
    const segundoTestigoApellido = this.accidentDataForm.get(
      'segundoTestigoApellido'
    );
    const segundoTestigoTipoDocumento = this.accidentDataForm.get(
      'segundoTestigoTipoDocumento'
    );
    const segundoTestigoNroDocumento = this.accidentDataForm.get(
      'segundoTestigoNroDocumento'
    );
    const segundoTestigoEmail = this.accidentDataForm.get(
      'segundoTestigoEmail'
    );
    const segundoTestigoTelefono = this.accidentDataForm.get(
      'segundoTestigoTelefono'
    );
    const segundoTestigoDireccion = this.accidentDataForm.get(
      'segundoTestigoDireccion'
    );
    const segundoTestigoAmpliacionTestigo = this.accidentDataForm.get(
      'segundoTestigoAmpliacionTestigos'
    );

    //Mapeo de los campos
    if (this.accidentDetail.segundoTestigo) {
      segundoTestigoNombre.patchValue(
        this.accidentDetail.segundoTestigo.nombre
      );
      segundoTestigoApellido.patchValue(
        this.accidentDetail.segundoTestigo.apellido
      );
      segundoTestigoTipoDocumento.patchValue(
        this.accidentDetail.segundoTestigo.tipoDocumento
      );
      segundoTestigoNroDocumento.patchValue(
        this.accidentDetail.segundoTestigo.nroDocumento
      );
      segundoTestigoEmail.patchValue(this.accidentDetail.segundoTestigo.email);
      segundoTestigoTelefono.patchValue(
        this.accidentDetail.segundoTestigo.telefono
      );
      segundoTestigoDireccion.patchValue(
        this.accidentDetail.segundoTestigo.direccion
      );
      segundoTestigoAmpliacionTestigo.patchValue(
        this.accidentDetail.segundoTestigo.ampliacionTestigos
      );
    }
  }

  private _parseDatosAccidente_Cuestionario(): void {
    // Get form data
    const cuestionarioFechaLlegada = this.accidentDataForm.get(
      'cuestionarioFechaLlegada'
    );
    const cuestionarioFechaHurto = this.accidentDataForm.get(
      'cuestionarioFechaHurto'
    );
    const cuestionarioLugar = this.accidentDataForm.get('cuestionarioLugar');
    const cuestionarioNroJuegoLlaves = this.accidentDataForm.get(
      'cuestionarioNroJuegoLlaves'
    );
    const cuestionarioCantidadLlaves = this.accidentDataForm.get(
      'cuestionarioCantidadLlaves'
    );
    const cuestionarioIndiciosForcejeo = this.accidentDataForm.get(
      'cuestionarioIndiciosForcejeo'
    );
    const cuestionarioSistemasSeguridad = this.accidentDataForm.get(
      'cuestionarioSistemasSeguridad'
    );
    const cuestionarioCantidadActivadores = this.accidentDataForm.get(
      'cuestionarioCantidadActivadores'
    );
    const cuestionarioExtravioJuegoLlaves = this.accidentDataForm.get(
      'cuestionarioExtravioJuegoLlaves'
    );
    const cuestionarioOtraPersona = this.accidentDataForm.get(
      'cuestionarioOtraPersona'
    );
    const cuestionarioOtraPersonaDuranteHurto = this.accidentDataForm.get(
      'cuestionarioOtraPersonaDuranteHurto'
    );
    const cuestionarioColorVehiculo = this.accidentDataForm.get(
      'cuestionarioColorVehiculo'
    );
    const cuestionarioCirculacionVehiculo = this.accidentDataForm.get(
      'cuestionarioCirculacionVehiculo'
    );
    const cuestionarioFrenteRadio = this.accidentDataForm.get(
      'cuestionarioFrenteRadio'
    );
    const cuestionarioMarcaRadio = this.accidentDataForm.get(
      'cuestionarioMarcaRadio'
    );
    const cuestionarioObservaciones = this.accidentDataForm.get(
      'cuestionarioObservaciones'
    );

    //Mapeo de los campos
    if (this.accidentDetail.cuestionarioHurtoTotal) {
      cuestionarioFechaLlegada.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal.fechaHoraLlegada
      );
      cuestionarioFechaHurto.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal.fechaHoraAvisoHurto
      );
      cuestionarioLugar.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal.lugarHecho
      );
      cuestionarioNroJuegoLlaves.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal
          .cuantosJuegosLlavesPoseeEsteMomento
      );
      cuestionarioCantidadLlaves.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal.llaveroCuantasLlavesConsta
      );
      cuestionarioIndiciosForcejeo.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal.indiciosForcejeo
      );
      cuestionarioSistemasSeguridad.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal.sistemaSeguridad
      );
      cuestionarioCantidadActivadores.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal
          .cantidadActivadoresPoseeEsteMomento
      );
      cuestionarioExtravioJuegoLlaves.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal.extravioOHurtoJuegoLlaves
      );
      cuestionarioOtraPersona.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal.otraPersonaCuandoDejoVehiculo
      );
      cuestionarioOtraPersonaDuranteHurto.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal
          .otraPersonaCuandoConstatoHurto
      );
      cuestionarioColorVehiculo.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal.colorVehiculoPresentabaDaños
      );
      cuestionarioCirculacionVehiculo.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal.dondeCirculaVehiculo
      );
      cuestionarioFrenteRadio.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal.presentaFrenteRadio
      );
      cuestionarioMarcaRadio.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal.marcaModeloRadio
      );
      cuestionarioObservaciones.patchValue(
        this.accidentDetail.cuestionarioHurtoTotal.observaciones
      );
    }
  }

  // Function to update the chasis number, only editable when the siniestro
  // failed because el vehiculo no fue encontrado (motivoFallo)
  public _updateEditAccidenteData(): void {
    if (
      this.tabAction === this.tabActionList.EDIT &&
      this.puedeEditarMatriculaYChasis
    ) {
      this.accidentEditableData.nroChasis =
        this.accidentDataForm.get('numeroChasis').value;
      this.editAccidentTabData.emit(this.accidentEditableData);
    }
  }

  // Function to update the matricula, only editable when the siniestro
  // failed by the Hana-GetCarDetails "sin resultados" error
  public _updateEditMatricula(): void {
    if (
      this.tabAction === this.tabActionList.EDIT &&
      this.puedeEditarMatriculaYChasis
    ) {
      this.accidentEditableData.matricula =
        this.accidentDataForm.get('matricula').value;
      this.editAccidentTabData.emit(this.accidentEditableData);
    }
  }

  // Function to update the fecha/hora de ocurrencia, only editable when the
  // siniestro failed by inconsistencia de fechas (fecha ocurrencia posterior
  // a la fecha de denuncia)
  public _updateEditFechaHoraOcurrencia(): void {
    if (
      this.tabAction !== this.tabActionList.EDIT ||
      !this.puedeEditarFechaHoraOcurrencia
    ) {
      return;
    }

    const fecha = this.accidentDataForm.get('fechaDeOcurrencia').value;
    const horaControl = this.accidentDataForm.get('horaDeOcurrencia');
    const hora = horaControl.value;

    const esFechaValida =
      fecha instanceof Date &&
      !isNaN(fecha.getTime()) &&
      (!this.maxFechaOcurrencia || fecha <= this.maxFechaOcurrencia);
    const esHoraValida = !!hora && !ValidateTime(horaControl as any);

    if (esFechaValida && esHoraValida) {
      this.accidentEditableData.fechaOcurrencia = this._combinarFechaHora(
        fecha,
        hora
      );
    } else {
      delete this.accidentEditableData.fechaOcurrencia;
    }
    this.editAccidentTabData.emit(this.accidentEditableData);
  }

  private _combinarFechaHora(fecha: Date, hora: string): string {
    const [horas, minutos, segundos] = hora.split(':').map(Number);
    const fechaHora = new Date(fecha);
    fechaHora.setHours(horas, minutos, segundos, 0);
    return fechaHora.toISOString();
  }

  private _validateMaxFechaOcurrencia(control: FormControl): any {
    if (!control.value || !this.maxFechaOcurrencia) return null;
    return control.value > this.maxFechaOcurrencia ? { maxDate: true } : null;
  }

  // Al menos uno de los dos datos (matricula o numeroChasis) debe existir
  // para poder reprocesar el siniestro
  public get vehiculoDatosIncompletos(): boolean {
    if (!this.puedeEditarMatriculaYChasis) return false;
    const matricula = this.accidentDataForm.get('matricula').value;
    const numeroChasis = this.accidentDataForm.get('numeroChasis').value;
    return !matricula && !numeroChasis;
  }

  public get matriculaHasError(): boolean {
    const control = this.accidentDataForm.get('matricula');
    return (
      (control.touched || this.attemptedReprocess) &&
      (control.invalid || this.vehiculoDatosIncompletos)
    );
  }

  public get matriculaErrorMessage(): string {
    const control = this.accidentDataForm.get('matricula');
    if (control.invalid) return 'La matrícula no es válida';
    if (this.vehiculoDatosIncompletos) {
      return 'Debés completar la matrícula o el número de chasis';
    }
    return '';
  }

  public get numeroChasisHasError(): boolean {
    const control = this.accidentDataForm.get('numeroChasis');
    return (
      (control.touched || this.attemptedReprocess) &&
      (control.invalid || this.vehiculoDatosIncompletos)
    );
  }

  public get numeroChasisErrorMessage(): string {
    const control = this.accidentDataForm.get('numeroChasis');
    if (control.invalid) return 'El número de chasis no es válido';
    if (this.vehiculoDatosIncompletos) {
      return 'Debés completar la matrícula o el número de chasis';
    }
    return '';
  }

  public get fechaDeOcurrenciaHasError(): boolean {
    const control = this.accidentDataForm.get('fechaDeOcurrencia');
    return (control.touched || this.attemptedReprocess) && control.invalid;
  }

  public get fechaDeOcurrenciaErrorMessage(): string {
    const control = this.accidentDataForm.get('fechaDeOcurrencia');
    if (control.hasError('required')) {
      return 'Debés ingresar la fecha de ocurrencia';
    }
    if (control.hasError('maxDate')) {
      return 'La fecha de ocurrencia no puede ser posterior a la fecha de denuncia';
    }
    return '';
  }

  public get horaDeOcurrenciaHasError(): boolean {
    const control = this.accidentDataForm.get('horaDeOcurrencia');
    return (control.touched || this.attemptedReprocess) && control.invalid;
  }

  public get horaDeOcurrenciaErrorMessage(): string {
    const control = this.accidentDataForm.get('horaDeOcurrencia');
    if (control.hasError('required')) {
      return 'Debés ingresar la hora de ocurrencia';
    }
    return 'La hora debe tener el formato HH:mm:ss';
  }
}
